/**
 * Vote flow state machine hook
 *
 * Manages the voting flow: presenting → voting → selecting-reasons → revealing
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getRandomImage, submitVote, type SubmitVoteResult } from '../lib/api';
import { getFingerprint } from '../lib/fingerprint';
import { useSessionStats } from './useSessionStats';
import type { ImageStats, Contradiction, AiStatus } from '@doyoulikethis/shared';

// =============================================================================
// Types
// =============================================================================

export type VoteFlowState =
  | 'loading'
  | 'presenting'
  | 'selecting-reasons'
  | 'revealing'
  | 'no-more-images'
  | 'error';

export interface CurrentImage {
  id: string;
  title_en: string;
  title_ja: string | null;
  cdn_url: string;
  categories: Array<{
    id: string;
    display_name_en: string;
    display_name_ja: string | null;
  }>;
}

export interface RevealedImage extends CurrentImage {
  artist: string | null;
  source_url: string;
  ai_status: AiStatus;
  ai_notes: string | null;
  license: string | null;
  stats: ImageStats;
}

export interface VoteFlowData {
  state: VoteFlowState;
  currentImage: CurrentImage | null;
  revealedImage: RevealedImage | null;
  userVote: boolean | null;
  userReasons: string[];
  contradiction: Contradiction | null;
  imageCount: number;
  error: string | null;
}

export interface VoteFlowActions {
  vote: (liked: boolean) => void;
  submitReasons: (reasons: string[]) => Promise<void>;
  skip: () => void;
  nextImage: () => void;
  retry: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useVoteFlow(): VoteFlowData & VoteFlowActions {
  // State
  const [state, setState] = useState<VoteFlowState>('loading');
  const [currentImage, setCurrentImage] = useState<CurrentImage | null>(null);
  const [revealedImage, setRevealedImage] = useState<RevealedImage | null>(null);
  const [userVote, setUserVote] = useState<boolean | null>(null);
  const [userReasons, setUserReasons] = useState<string[]>([]);
  const [contradiction, setContradiction] = useState<Contradiction | null>(null);
  const [imageCount, setImageCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Timing refs
  const imageLoadTimeRef = useRef<number>(0);
  const voteTimeRef = useRef<number>(0);

  // Session stats
  const { votedImageIds, recordVote, recordSkip } = useSessionStats();

  // =============================================================================
  // Load next image
  // =============================================================================

  const loadNextImage = useCallback(async () => {
    setState('loading');
    setCurrentImage(null);
    setRevealedImage(null);
    setUserVote(null);
    setUserReasons([]);
    setContradiction(null);
    setError(null);

    try {
      const image = await getRandomImage(votedImageIds);

      if (!image) {
        setState('no-more-images');
        return;
      }

      setCurrentImage(image);
      setImageCount((prev) => prev + 1);
      imageLoadTimeRef.current = Date.now();
      setState('presenting');
    } catch (err) {
      console.error('Failed to load image:', err);
      setError(err instanceof Error ? err.message : 'Failed to load image');
      setState('error');
    }
  }, [votedImageIds]);

  // Load first image on mount
  useEffect(() => {
    loadNextImage();
  }, []);

  // =============================================================================
  // Vote action
  // =============================================================================

  const vote = useCallback((liked: boolean) => {
    voteTimeRef.current = Date.now();
    setUserVote(liked);
    setState('selecting-reasons');
  }, []);

  // =============================================================================
  // Submit reasons action
  // =============================================================================

  const submitReasons = useCallback(
    async (reasons: string[]) => {
      if (!currentImage || userVote === null) return;

      setUserReasons(reasons);
      setState('loading');

      const reasonsTime = Date.now();
      const timeToVoteMs = voteTimeRef.current - imageLoadTimeRef.current;
      const timeToReasonsMs = reasonsTime - voteTimeRef.current;

      try {
        const fingerprint = await getFingerprint();

        const result: SubmitVoteResult = await submitVote({
          imageId: currentImage.id,
          liked: userVote,
          reasons,
          fingerprint,
          timeToVoteMs,
          timeToReasonsMs,
        });

        // Record in session stats
        recordVote({
          imageId: currentImage.id,
          liked: userVote,
          reasons,
          aiStatus: result.image.ai_status,
          categories: currentImage.categories.map((c) => c.id),
        });

        // Set revealed image with full details
        setRevealedImage({
          ...currentImage,
          artist: result.image.artist,
          source_url: result.image.source_url,
          ai_status: result.image.ai_status,
          ai_notes: result.image.ai_notes,
          license: result.image.license,
          stats: result.image.stats,
        });

        setContradiction(result.contradiction);
        setState('revealing');
      } catch (err) {
        console.error('Failed to submit vote:', err);
        setError(err instanceof Error ? err.message : 'Failed to submit vote');
        setState('error');
      }
    },
    [currentImage, userVote, recordVote]
  );

  // =============================================================================
  // Skip action
  // =============================================================================

  const skip = useCallback(() => {
    if (currentImage) {
      recordSkip(currentImage.id);
    }
    loadNextImage();
  }, [currentImage, recordSkip, loadNextImage]);

  // =============================================================================
  // Next image action
  // =============================================================================

  const nextImage = useCallback(() => {
    loadNextImage();
  }, [loadNextImage]);

  // =============================================================================
  // Retry action
  // =============================================================================

  const retry = useCallback(() => {
    loadNextImage();
  }, [loadNextImage]);

  // =============================================================================
  // Return
  // =============================================================================

  return {
    // Data
    state,
    currentImage,
    revealedImage,
    userVote,
    userReasons,
    contradiction,
    imageCount,
    error,

    // Actions
    vote,
    submitReasons,
    skip,
    nextImage,
    retry,
  };
}
